import React from 'react';
import { useTranslation } from 'react-i18next';
import { XMarkIcon, ChevronDoubleDownIcon } from '@heroicons/react/24/outline';
import { TERMS_SECTIONS } from './TermsContent';

interface TermsOfServiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onApprove?: () => void;
    mode?: 'accept' | 'view';
}

const TermsOfServiceModal: React.FC<TermsOfServiceModalProps> = ({
    isOpen,
    onClose,
    onApprove,
    mode = 'accept',
}) => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'he' || i18n.language === 'ar';
    const contentDirection = isRTL ? 'rtl' : 'ltr';

    const [hasScrolledToBottom, setHasScrolledToBottom] = React.useState(false);
    const contentRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (isOpen) {
            setHasScrolledToBottom(false);
        }
    }, [isOpen]);

    React.useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
        if (scrollHeight - scrollTop - clientHeight < 20) {
            setHasScrolledToBottom(true);
        }
    };

    const scrollToBottom = () => {
        if (contentRef.current) {
            contentRef.current.scrollTo({ top: contentRef.current.scrollHeight, behavior: 'smooth' });
        }
    };

    const handleApprove = () => {
        if (hasScrolledToBottom && onApprove) {
            onApprove();
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md"
            onClick={onClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby="terms-modal-title"
        >
            <div
                className="relative bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl overflow-hidden border border-brand-100 dark:border-brand-900/30 ring-1 ring-black/5 flex flex-col"
                style={{ width: '50vw', height: '66vh' }}
                onClick={(e) => e.stopPropagation()}
                dir={isRTL ? 'rtl' : 'ltr'}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-brand-500 to-brand-600 dark:from-brand-600 dark:to-brand-700 flex-shrink-0">
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 transition-all duration-200"
                        aria-label={t('common.close')}
                    >
                        <XMarkIcon className="w-4 h-4 text-white" />
                    </button>
                    <h2 id="terms-modal-title" className="text-base font-bold text-white tracking-wide">
                        {t('auth.termsOfService')}
                    </h2>
                </div>

                {/* Intro bar */}
                <div className="px-6 py-3 bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 text-xs text-neutral-500 dark:text-neutral-400 flex-shrink-0" dir={contentDirection}>
                    <span className="font-medium">בין: </span>
                    <span className="text-brand-600 dark:text-brand-400">סנדל ישראל בע"מ, ח.פ. 514628403</span>
                    {' '}(להלן: "החברה") לבין: המשתמש המאשר הסכם זה (להלן: "הלקוח")
                </div>

                {/* Scrollable sections */}
                <div className="relative flex-1 overflow-hidden">
                    <div
                        ref={contentRef}
                        onScroll={handleScroll}
                        className="h-full overflow-y-auto"
                        dir={contentDirection}
                    >
                        {TERMS_SECTIONS.map((section, idx) => (
                            <div
                                key={idx}
                                className="px-6 py-4 border-b border-neutral-100 dark:border-neutral-800"
                            >
                                <h3 className="text-sm font-bold text-brand-600 dark:text-brand-400 mb-3 pb-2 border-b border-neutral-100 dark:border-neutral-700" style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                                    {section.title}
                                </h3>
                                <div className="space-y-2" >
                                    {section.items.map((item, itemIdx) => (
                                        // the direction is reversed because the text is in Hebrew
                                        <div key={itemIdx} className="flex gap-2 text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed" style={{ direction: !isRTL ? 'rtl' : 'ltr' }}>
                                            <span className="text-neutral-400 dark:text-neutral-600 font-mono text-xs pt-0.5 flex-shrink-0">
                                                {idx + 1}.{itemIdx + 1}
                                            </span>
                                            <span>{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Scroll-to-bottom FAB — only in accept mode */}
                    {mode === 'accept' && !hasScrolledToBottom && (
                        <button
                            onClick={scrollToBottom}
                            className="absolute bottom-4 left-1/2 -translate-x-1/2 p-2 bg-brand-500 hover:bg-brand-600 text-white rounded-full shadow-lg transition-all duration-200 animate-bounce z-10"
                            aria-label={t('common.readToApprove')}
                        >
                            <ChevronDoubleDownIcon className="w-5 h-5" />
                        </button>
                    )}
                </div>

                {/* Footer */}
                <div
                    className="flex justify-end px-5 py-3 border-t border-neutral-100 dark:border-neutral-700/50 bg-neutral-50/50 dark:bg-neutral-900/30 gap-3 flex-shrink-0"
                    style={{ direction: 'ltr' }}
                >
                    <button
                        onClick={onClose}
                        className="px-4 py-1.5 text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 text-sm font-medium transition-colors"
                    >
                        {mode === 'accept' ? t('common.cancel') : t('common.close')}
                    </button>
                    {mode === 'accept' && (
                        <button
                            onClick={handleApprove}
                            disabled={!hasScrolledToBottom}
                            className={`px-6 py-1.5 text-sm font-semibold rounded-lg transition-all duration-200 shadow-sm
                                ${hasScrolledToBottom
                                    ? 'bg-brand-500 hover:bg-brand-600 active:bg-brand-700 text-white cursor-pointer'
                                    : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-400 dark:text-neutral-500 cursor-not-allowed'
                                }`}
                        >
                            {hasScrolledToBottom ? t('common.approve') : t('common.readToApprove')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TermsOfServiceModal;
